import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import BackgroundLocationService from '../services/BackgroundLocationService';

interface LocationSharingContextType {
  isSharing: boolean;
  sharingId: string | null;
  startSharing: () => Promise<void>;
  stopSharing: () => Promise<void>;
  isBackgroundTracking: boolean;
}

const LocationSharingContext = createContext<LocationSharingContextType | undefined>(undefined);

export const useLocationSharing = () => {
  const context = useContext(LocationSharingContext);
  if (context === undefined) {
    throw new Error('useLocationSharing must be used within a LocationSharingProvider');
  }
  return context;
};

interface LocationSharingProviderProps {
  children: ReactNode;
}

export const LocationSharingProvider: React.FC<LocationSharingProviderProps> = ({ children }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [isBackgroundTracking, setIsBackgroundTracking] = useState(false);
  const [backgroundService] = useState(() => BackgroundLocationService.getInstance());

  useEffect(() => {
    // Initialize background service and check for existing tracking
    const initializeBackgroundTracking = async () => {
      try {
        console.log('LocationSharingContext: Initializing background tracking service');
        
        // Check if background tracking is already active
        const isCurrentlyTracking = backgroundService.isTracking();
        if (isCurrentlyTracking) {
          console.log('LocationSharingContext: Background tracking is already active');
          setIsSharing(true);
          setIsBackgroundTracking(true);
          
          const state = backgroundService.getTrackingState();
          setSharingId(state.sharingId);
          
          console.log('LocationSharingContext: Restored background tracking state:', state);
        } else {
          // Try to restore tracking from saved state
          const restored = await backgroundService.restoreTracking();
          if (restored) {
            console.log('LocationSharingContext: Successfully restored background tracking');
            setIsSharing(true);
            setIsBackgroundTracking(true);
            
            const state = backgroundService.getTrackingState();
              setSharingId(state.sharingId);
          }
        }
      } catch (error) {
        console.error('LocationSharingContext: Error initializing background tracking:', error);
      }
    };

    initializeBackgroundTracking();
  }, [backgroundService]);

  const startSharing = async () => {
    try {
      console.log('LocationSharingContext: Starting location sharing');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get user profile
      const { data: profile, error: profileError } = await supabase
            .from('users')
            .select('username, user_group')
        .eq('id', user.id)
            .single();
          
      if (profileError || !profile) {
        throw new Error('Failed to get user profile');
      }

      // Generate sharing ID
      const newSharingId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Start background tracking
      const success = await backgroundService.startTracking(
        newSharingId,
        user.id,
        profile.username,
        profile.user_group
      );

      if (success) {
        setIsSharing(true);
        setSharingId(newSharingId);
        setIsBackgroundTracking(true);
        
        console.log('LocationSharingContext: Location sharing started successfully');
        console.log('LocationSharingContext: Background tracking is now active');
        console.log('LocationSharingContext: Location will continue updating even when page is minimized or navigated away');
      } else {
        throw new Error('Failed to start background tracking');
      }

    } catch (error) {
      console.error('LocationSharingContext: Error starting location sharing:', error);
      throw error;
    }
  };

  const stopSharing = async () => {
    try {
      console.log('LocationSharingContext: Stopping location sharing');
      
      // Stop background tracking
      await backgroundService.stopTracking();
      
      setIsSharing(false);
      setSharingId(null);
      setIsBackgroundTracking(false);
      
      console.log('LocationSharingContext: Location sharing stopped successfully');
      console.log('LocationSharingContext: Background tracking has been disabled');

    } catch (error) {
      console.error('LocationSharingContext: Error stopping location sharing:', error);
      throw error;
    }
  };

  const value: LocationSharingContextType = {
    isSharing,
    sharingId,
    startSharing,
    stopSharing,
    isBackgroundTracking
  };

  return (
    <LocationSharingContext.Provider value={value}>
      {children}
    </LocationSharingContext.Provider>
  );
}; 